import { PresetIncludes } from "../types";

// Server/runtime fields that belong to a concrete saved campaign and must NOT
// be frozen into a reusable preset. In particular `isEditable` is a backend
// flag describing whether an EXISTING campaign's message can still be edited;
// if it leaks into the preset it makes brand-new drafts read-only.
const MESSAGE_RUNTIME_FIELDS = [
  "id",
  "campaignId",
  "isEditable",
  "createdBy",
  "updatedBy",
  "dateCreated",
  "dateUpdated",
  "dateSend",
] as const;

// Strips server/runtime fields from each scheduled message so the preset
// stores only authoring data.
function sanitizePresetMessages(messages: unknown): unknown {
  if (!Array.isArray(messages)) return messages;
  return messages.map((message) => {
    if (!message || typeof message !== "object") return message;
    const sanitized: Record<string, unknown> = { ...(message as Record<string, unknown>) };
    for (const field of MESSAGE_RUNTIME_FIELDS) {
      delete sanitized[field];
    }
    return sanitized;
  });
}

// Applies the include-flags to builder form values, producing the
// content blob that gets persisted on a campaign preset.
export function buildPresetContent(
  formValues: Record<string, unknown>,
  includes: PresetIncludes
): Record<string, unknown> {
  const content: Record<string, unknown> = {};

  if (includes.messages)
    content.scheduledMessages = sanitizePresetMessages(formValues.scheduledMessages);

  if (includes.keywords) {
    if (formValues.keywordIds !== undefined)
      content.keywordIds = formValues.keywordIds;
  }

  if (includes.optOut) {
    if (formValues.optOutText !== undefined)
      content.optOutText = formValues.optOutText;
    if (formValues.isOptOutEnabled !== undefined)
      content.isOptOutEnabled = formValues.isOptOutEnabled;
  }

  if (includes.audience) {
    if (formValues.groupIds !== undefined)
      content.groupIds = formValues.groupIds;
  }

  // appointmentTypeId is part of the campaign config (reminder campaigns target
  // by appointment type), not audience, so it is always carried over when set.
  if (formValues.appointmentTypeId !== undefined)
    content.appointmentTypeId = formValues.appointmentTypeId;

  return content;
}
